/**
 * What a CARA search is, from the user's side — the shape the data browser
 * keeps in state and the CARA Source node persists on its config.
 *
 * Its own module rather than part of `CaraSearchBuilder.tsx` so both a
 * component and a node's config can import it without pulling a component in
 * (and so the builder file exports only its component).
 */

import type { CaraFilterSpec } from "@/shared/api/integration-cara";

export interface CaraSearchSelection {
  /** "builder" — pick an object and filter it; "body" — write the request body. */
  mode: "builder" | "body";
  typeName: string;
  /** Columns to keep, in order. Empty means every column the records carry. */
  fields: string[];
  filters: CaraFilterSpec[];
  text: string;
  searchInContent: boolean;
  /** The raw CARA search body, used in "body" mode. */
  body: string;
}

export const EMPTY_CARA_SEARCH: CaraSearchSelection = {
  mode: "builder",
  typeName: "",
  fields: [],
  filters: [],
  text: "",
  searchInContent: false,
  body: "",
};

/** Read a selection out of a loosely-typed store (a node's saved config). */
export function caraSelectionFrom(config: Record<string, unknown>): CaraSearchSelection {
  return {
    mode: config.mode === "body" ? "body" : "builder",
    typeName: String(config.typeName ?? ""),
    fields: Array.isArray(config.fields) ? config.fields.map(String) : [],
    filters: Array.isArray(config.filters) ? (config.filters as CaraFilterSpec[]) : [],
    text: String(config.text ?? ""),
    searchInContent: Boolean(config.searchInContent),
    body: String(config.body ?? ""),
  };
}

/** Whether a selection names something searchable yet. */
export function caraSearchReady(search: CaraSearchSelection): boolean {
  return search.mode === "body" ? search.body.trim().length > 0 : Boolean(search.typeName);
}
